import { useCallback, useEffect, useMemo, useState } from "react";
import { FileText, Loader2, Trash2, Upload, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha, fmtHora } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function fmtTamano(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const ALCANCE_LABEL = { empresa: "Toda la empresa", departamento: "Departamento", trabajador: "Trabajador" };

function alcancesPermitidos(categoria) {
  if (!categoria) return ["trabajador", "departamento", "empresa"];
  if (categoria.alcance_minimo === "trabajador") return ["trabajador"];
  if (categoria.alcance_minimo === "departamento") return ["trabajador", "departamento"];
  return ["trabajador", "departamento", "empresa"];
}

function FiltroSecciones({ categorias, seccion, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      <Button
        type="button"
        size="sm"
        variant={seccion === "todas" ? "default" : "outline"}
        onClick={() => onChange("todas")}
      >
        Todas
      </Button>
      {categorias.map((c) => (
        <Button
          key={c.id}
          type="button"
          size="sm"
          variant={seccion === String(c.id) ? "default" : "outline"}
          onClick={() => onChange(String(c.id))}
        >
          {c.nombre}
        </Button>
      ))}
    </div>
  );
}

function ListaDocumentos({ items, onDetalle, onEliminar }) {
  if (items.length === 0) {
    return (
      <p className="rounded-xl border border-dashed border-zinc-200 py-12 text-center text-sm text-zinc-500">
        No hay documentos en esta sección.
      </p>
    );
  }
  return (
    <div className="space-y-3">
      {items.map((doc) => (
        <div key={doc.id} className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium text-zinc-900">{doc.concepto}</p>
              <p className="text-xs text-zinc-500">{doc.nombre_archivo} · {fmtTamano(doc.tamano_bytes)}</p>
              <p className="mt-1 text-xs text-zinc-400">
                Subido {fmtFecha(doc.created_at)} {fmtHora(doc.created_at)} por {doc.subido_por || "—"}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {doc.categoria && <Badge variant="secondary">{doc.categoria}</Badge>}
                <Badge variant="outline">{ALCANCE_LABEL[doc.alcance] || doc.alcance}</Badge>
                {doc.destinatario && <Badge variant="outline">{doc.destinatario}</Badge>}
                {doc.departamento && <Badge variant="outline">{doc.departamento}</Badge>}
                <Badge className="bg-emerald-100 text-emerald-800">
                  {doc.total_descargas} descarga{doc.total_descargas === 1 ? "" : "s"}
                </Badge>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => onDetalle(doc)}>
                <Users className="mr-1 h-3.5 w-3.5" />
                Descargas
              </Button>
              <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => onEliminar(doc.id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Documentos() {
  const [items, setItems] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [seccion, setSeccion] = useState("todas");
  const [cargando, setCargando] = useState(true);
  const [open, setOpen] = useState(false);
  const [concepto, setConcepto] = useState("");
  const [archivo, setArchivo] = useState(null);
  const [categoriaId, setCategoriaId] = useState("");
  const [alcance, setAlcance] = useState("empresa");
  const [usuarioId, setUsuarioId] = useState("none");
  const [departamentoId, setDepartamentoId] = useState("none");
  const [empleados, setEmpleados] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [subiendo, setSubiendo] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [descargas, setDescargas] = useState([]);
  const [cargandoDesc, setCargandoDesc] = useState(false);

  const categoriaSeleccionada = useMemo(
    () => categorias.find((c) => String(c.id) === categoriaId),
    [categorias, categoriaId],
  );
  const permitidos = alcancesPermitidos(categoriaSeleccionada);

  const cargar = useCallback(async () => {
    try {
      const params = seccion !== "todas" ? { categoria_id: seccion } : {};
      const { data } = await api.get("/documentos", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCargando(false);
    }
  }, [seccion]);

  useEffect(() => {
    setCargando(true);
    cargar();
  }, [cargar]);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const [cats, u, d] = await Promise.all([
          api.get("/documentos/categorias"),
          api.get("/usuarios"),
          api.get("/departamentos"),
        ]);
        if (cancelado) return;
        const lista = cats.data?.items || [];
        setCategorias(lista);
        if (lista.length) setCategoriaId(String(lista[0].id));
        const usuarios = Array.isArray(u.data) ? u.data : [];
        setEmpleados(usuarios.filter((x) => x.rol === "worker" && x.activo !== false));
        setDepartamentos(Array.isArray(d.data) ? d.data : []);
      } catch (e) {
        if (!cancelado) toast.error(formatApiError(e));
      }
    })();
    return () => { cancelado = true; };
  }, []);

  useEffect(() => {
    if (!categoriaSeleccionada) return;
    if (!permitidos.includes(alcance)) setAlcance(permitidos[0]);
  }, [categoriaSeleccionada, permitidos, alcance]);

  const abrirSubida = () => {
    if (!categorias.length) {
      toast.error("Espera a que carguen las secciones");
      return;
    }
    setCategoriaId(String(categorias[0].id));
    setOpen(true);
  };

  const abrirDetalle = async (doc) => {
    setDetalle(doc);
    setCargandoDesc(true);
    try {
      const { data } = await api.get(`/documentos/${doc.id}/descargas`);
      setDescargas(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
      setDescargas([]);
    } finally {
      setCargandoDesc(false);
    }
  };

  const subir = async () => {
    if (!concepto.trim() || !archivo || !categoriaId) {
      toast.error("Indica sección, concepto y selecciona un PDF");
      return;
    }
    if ((alcance === "trabajador" || permitidos.length === 1) && usuarioId === "none") {
      toast.error("Selecciona el trabajador destinatario");
      return;
    }
    if (alcance === "departamento" && departamentoId === "none") {
      toast.error("Selecciona el departamento destinatario");
      return;
    }

    const fd = new FormData();
    fd.append("concepto", concepto.trim());
    fd.append("categoria_id", categoriaId);
    fd.append("archivo", archivo);
    if ((alcance === "trabajador" || permitidos.length === 1) && usuarioId !== "none") {
      fd.append("usuario_id", usuarioId);
    }
    if (alcance === "departamento" && departamentoId !== "none") fd.append("departamento_id", departamentoId);

    setSubiendo(true);
    try {
      await api.post("/documentos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento publicado y notificado a los trabajadores");
      setOpen(false);
      setConcepto("");
      setArchivo(null);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubiendo(false);
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar este documento?")) return;
    try {
      await api.delete(`/documentos/${id}`);
      toast.success("Documento eliminado");
      if (detalle?.id === id) setDetalle(null);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        icon={FileText}
        title="Gestión documental"
        subtitle="Publica PDFs por sección (nóminas, contratos, cursos…) con control de acceso"
        sectionKey="documentos"
        brandGradient={false}
      >
        <Button onClick={abrirSubida} className="gap-2 rounded-lg bg-white/20 text-white hover:bg-white/30">
          <Upload className="h-4 w-4" />
          Subir PDF
        </Button>
      </PageHeader>

      <FiltroSecciones categorias={categorias} seccion={seccion} onChange={setSeccion} />

      <div>
        {cargando ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
          </div>
        ) : (
          <ListaDocumentos items={items} onDetalle={abrirDetalle} onEliminar={eliminar} />
        )}
      </div>

      {open && (
        <Dialog open onOpenChange={setOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Subir documento PDF</DialogTitle>
              <DialogDescription>
                Elige la sección y el destinatario. Los trabajadores solo podrán descargar lo que les corresponda.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {categorias.length > 0 && categoriaId && (
                <div className="space-y-1.5">
                  <Label>Sección *</Label>
                  <Select value={categoriaId} onValueChange={setCategoriaId}>
                    <SelectTrigger><SelectValue placeholder="Sección" /></SelectTrigger>
                    <SelectContent>
                      {categorias.map((c) => (
                        <SelectItem key={c.id} value={String(c.id)}>{c.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {categoriaSeleccionada && (
                    <p className="text-xs text-zinc-500">
                      {categoriaSeleccionada.alcance_minimo === "trabajador" && "Obligatorio asignar a un trabajador (p. ej. nóminas)."}
                      {categoriaSeleccionada.alcance_minimo === "departamento" && "Debe ir a un trabajador o departamento, no a toda la empresa."}
                      {categoriaSeleccionada.alcance_minimo === "empresa" && "Puede publicarse para toda la empresa."}
                    </p>
                  )}
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Concepto *</Label>
                <Input value={concepto} onChange={(e) => setConcepto(e.target.value)} placeholder="Ej. Nómina marzo 2026" />
              </div>
              <div className="space-y-1.5">
                <Label>Archivo PDF *</Label>
                <Input type="file" accept="application/pdf,.pdf" onChange={(e) => setArchivo(e.target.files?.[0] || null)} />
              </div>
              {permitidos.length > 1 && (
                <div className="space-y-1.5">
                  <Label>Destinatarios</Label>
                  <Select value={alcance} onValueChange={setAlcance}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {permitidos.includes("empresa") && <SelectItem value="empresa">Toda la empresa</SelectItem>}
                      {permitidos.includes("departamento") && <SelectItem value="departamento">Un departamento</SelectItem>}
                      {permitidos.includes("trabajador") && <SelectItem value="trabajador">Un trabajador</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {alcance === "departamento" && (
                <Select value={departamentoId} onValueChange={setDepartamentoId}>
                  <SelectTrigger><SelectValue placeholder="Departamento" /></SelectTrigger>
                  <SelectContent>
                    {departamentos.map((d) => (
                      <SelectItem key={d.id} value={String(d.id)}>{d.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {(alcance === "trabajador" || permitidos.length === 1) && (
                <Select value={usuarioId} onValueChange={setUsuarioId}>
                  <SelectTrigger><SelectValue placeholder="Trabajador" /></SelectTrigger>
                  <SelectContent>
                    {empleados.map((u) => (
                      <SelectItem key={u.id} value={String(u.id)}>
                        {u.nombre} {u.apellidos}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button onClick={subir} disabled={subiendo}>{subiendo ? "Subiendo…" : "Publicar"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {detalle && (
        <Dialog open onOpenChange={(v) => !v && setDetalle(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Descargas — {detalle.concepto}</DialogTitle>
              <DialogDescription>Registro de quién ha descargado el documento.</DialogDescription>
            </DialogHeader>
            {cargandoDesc ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>
            ) : descargas.length === 0 ? (
              <p className="py-6 text-center text-sm text-zinc-500">Nadie lo ha descargado aún.</p>
            ) : (
              <ul className="max-h-64 space-y-2 overflow-y-auto">
                {descargas.map((d) => (
                  <li key={d.id} className="flex items-center justify-between rounded-lg border border-zinc-100 px-3 py-2 text-sm">
                    <span>{d.usuario}</span>
                    <span className="text-xs text-zinc-500">{fmtFecha(d.descargado_en)} {fmtHora(d.descargado_en)}</span>
                  </li>
                ))}
              </ul>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
